import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'

import { ResultCode } from '../../../domain/authentication.model'
import { NotificationService, SeverityType } from '../../../services/notification.service'
import { AppService } from '../../../services/prod/app.service'
import { I18nService, LgType } from '../../../services/prod/i18n.service'
import { ProducerManagementService } from '../../../services/producer-management.service'
import { IColorGroup, SetColorsService } from '../../../services/set-colors.service'
import { AppType } from '../../../types/app.type'
import { ManageGroupType, ThemeTitleType, ThemeType } from '../../../types/color-settings.type'
import { ContactsResultMessageType, ContactType, IBeatmakerData, SocNetworkType } from '../../../types/common.type'

@Component({
  selector: 'bmcore-color-customizer',
  templateUrl: './bmcore-color-customizer.component.html',
  styleUrls: ['./bmcore-color-customizer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BmcoreColorCustomizerComponent implements OnInit {
  @Input() appType: AppType

  lg: LgType
  groups: IColorGroup[]
  currentGroupTitle = ''
  manageGroups: ManageGroupType[] = []

  isSettingsShow = false

  currenThemeTitle: ThemeTitleType
  colorThemes: ThemeType[] = []
  disableSaveSettingsBtn = true

  beatmakerData: IBeatmakerData
  socContacts: ContactType[] = [
    { link: '', type: 'INSTAGRAM' },
    { link: '', type: 'TWITTER' },
    { link: '', type: 'YOUTUBE' },
    { link: '', type: 'VK' },
    { link: '', type: 'SOUNDCLOUD' },
    { link: '', type: 'FACEBOOK' },
    { link: '', type: 'TWITCH' },
    { link: '', type: 'TIKTOK' },
    { link: '', type: 'BEATMAKERS.TV' }
  ]

  contactsResultMessage: ContactsResultMessageType

  constructor(
    private setColorsService: SetColorsService,
    private i18nService: I18nService,
    private notificationService: NotificationService,
    private producerManagementService: ProducerManagementService,
    private appService: AppService
  ) {}

  ngOnInit(): void {
    this.lg = this.i18nService.getLanguageForPage()
    this.groups = this.setColorsService.getGroups()
    this.getBeatmakerData()

    this.manageGroups = [
      { title: this.lg.colorCustomizer.manageBlock.beatSorting },
      { title: this.lg.colorCustomizer.manageBlock.bgImages },
      { title: this.lg.colorCustomizer.manageBlock.socMedia }
    ]

    this.currenThemeTitle = this.setColorsService.currentThemeTitle

    this.colorThemes = [
      { descr: this.lg.colorCustomizer.themeSelect.themes.blackRed, title: 'dark-red' },
      { descr: this.lg.colorCustomizer.themeSelect.themes.light, title: 'light' },
      { descr: this.lg.colorCustomizer.themeSelect.themes.blackOrange, title: 'dark-orange' },
      { descr: this.lg.colorCustomizer.themeSelect.themes.purplePink, title: 'dark-pink' },
      { descr: this.lg.colorCustomizer.themeSelect.themes.neon, title: 'dark-neon' }
    ]

    this.setColorsService.colorSettingsSubject.subscribe(value => {
      this.isSettingsShow = value
    })
  }

  getBeatmakerData() {
    this.appService.beatMakerDataSubject.subscribe(res => {
      if (res?.beatmakerId) {
        this.beatmakerData = res

        const transformContacts = res.contacts.map(c => {
          return {
            link: c.url,
            type: c.type
          }
        })
        this.mergeArrays(transformContacts)
      }
    })
  }

  mergeArrays(transformContacts: ContactType[]) {
    // Merge two arrays of objects and de-duplicate based on property value
    // https://stackoverflow.com/a/37058222
    for (var i = 0, l = this.socContacts.length; i < l; i++) {
      for (var j = 0, ll = transformContacts.length; j < ll; j++) {
        if (this.socContacts[i].type === transformContacts[j].type) {
          this.socContacts.splice(i, 1, transformContacts[j])
          break
        }
      }
    }
  }

  closeSettings() {
    this.setColorsService.notifyComponent(false)
  }

  changeCurrentGroup(event: MouseEvent, title: string): void {
    if (this.currentGroupTitle === title) {
      // If clicked on the same group then close this group
      this.currentGroupTitle = ''
    } else {
      this.currentGroupTitle = title
    }
  }

  saveSettings() {
    this.setColorsService.sendSettings(this.appType, this.currenThemeTitle).subscribe(
      res => {
        if (res.resultCode === ResultCode.Success) {
          window.location.reload()
        } else {
          this.notificationService.handleError(res.messages[0])
        }
      },
      err => this.notificationService.handleError(err.message)
    )
  }

  changeTheme() {
    this.changeTitleTheme(this.currenThemeTitle)
    this.setColorsService.refreshStyleTag(this.currenThemeTitle)
    this.groups = this.setColorsService.getGroups()
  }

  changeTitleTheme(themeTitle: ThemeTitleType) {
    this.disableSaveSettingsBtn = false
    this.currenThemeTitle = themeTitle
  }

  updateContactsLocally(contact: ContactType) {
    // 1. Update array of contacts
    const itemIndex = this.socContacts.findIndex(el => el.type === contact.type)
    this.socContacts[itemIndex] = contact

    // 2. Remove empty (null, '') fields
    const socContactsEng = this.socContacts.filter(c => !!c.link)

    // 3. Create payload
    const payload = {
      eng: socContactsEng,
      ru: []
    }

    this.updateContacts(payload, contact.type)
  }

  updateContacts(payload: { [key: string]: ContactType[] }, type: SocNetworkType) {
    this.producerManagementService.updateContacts(payload).subscribe(
      res => {
        if (res.resultCode === ResultCode.Success) {
          this.updateContactsBlock()
          this.sendContactsResultMessage(
            type,
            this.lg.colorCustomizer.uploadContactsBlock.setSuccessMessage(type),
            'success'
          )
        } else {
          this.sendContactsResultMessage(type, `${type}: ${res.messages[0]}`, 'error')
        }
      },
      err => this.sendContactsResultMessage(type, `${type}: ${err.message}`, 'error')
    )
  }

  sendContactsResultMessage(type: SocNetworkType, message: string, severity: SeverityType) {
    this.contactsResultMessage = {
      type,
      message,
      severity
    }
  }

  updateContactsBlock() {
    this.appService.beatMakerDataSubject.next({
      ...this.beatmakerData,
      contacts: this.socContacts.map(c => ({ url: c.link, type: c.type }))
    })
  }

  resetSettings() {
    this.setColorsService.resetSettings()
  }
}
